import { useEffect, useState } from 'react'
import type { MoteStatus, MoteStatusReaction } from '@mote/api'
import { useMoteReactUnreactMutation } from '@mote/api'
import { TRACK_EVENT_NAMES } from '@mote/helpers'
import type { ListeningContext } from '../track'
import { useMoteTrackMutate } from '../track'

export type Tuple1<T> = [T]

function isTuple1<T extends any>(arr: T[]): arr is Tuple1<T> {
  return arr.length === 1
}

export type ReactionsState = MoteStatus['myReactions']

export interface ReactionsData {
  id: string
  createdByUserId: string
  listeningContext: ListeningContext
}

export interface UseReactions {
  myReactions: ReactionsState
  isEnabled: (reaction: MoteStatusReaction) => boolean
  react: (reaction: MoteStatusReaction) => Promise<void>
  clear: () => Promise<void>
}

export function useReactions(
  data: ReactionsData,
  myReactions: ReactionsState
): UseReactions {
  const moteTrackMutate = useMoteTrackMutate(
    data.listeningContext,
    data.id,
    data.createdByUserId
  )
  const moteReactUnReactMutation = useMoteReactUnreactMutation(data.id)
  const [myReactionsState, setMyReactionsState] =
    useState<ReactionsState>(myReactions)

  useEffect(() => {
    setMyReactionsState(myReactions)
  }, [myReactions])

  function getIsEnabled(reaction: MoteStatusReaction): boolean {
    return isTuple1(myReactionsState) && myReactionsState.includes(reaction)
  }

  function makeApiCall(reaction: string, nextIsEnabled: boolean) {
    return (
      //@ts-expect-error
      moteReactUnReactMutation
        .mutate({
          variables: {
            reaction,
            isEnabled: nextIsEnabled
          }
        })
        //@ts-expect-error
        .then(data => {
          if (data.error) {
            return Promise.reject(data.error)
          }
          return moteTrackMutate(
            nextIsEnabled
              ? TRACK_EVENT_NAMES.mote.action.reaction.added
              : TRACK_EVENT_NAMES.mote.action.reaction.removed,
            {
              reaction
            }
          )
        })
    )
  }

  async function react(reaction: MoteStatusReaction): Promise<void> {
    try {
      setMyReactionsState(getIsEnabled(reaction) ? [] : [reaction])
      await makeApiCall(reaction, !getIsEnabled(reaction))
    } catch {
      setMyReactionsState(myReactionsState)
    }
  }

  async function clear() {
    const currentReaction = myReactionsState[0]
    if (currentReaction) {
      await react(currentReaction)
    }
  }

  return {
    myReactions: myReactionsState,
    isEnabled: getIsEnabled,
    react,
    clear
  }
}
